import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  TaskAttachmentLinkDocument,
  TaskAttachmentLinkQuery,
  TaskAttachmentLinkQueryVariables,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';

interface ITaskAttachmentUrlHook {
  /** Свежая подписанная ссылка на файл, или `null` при отказе. */
  resolveUrl: (attachmentId: string) => Promise<string | null>;
  /** Открыть файл в новой вкладке. */
  openFile: (attachmentId: string) => Promise<void>;
  /** Какой файл сейчас разрешается — им гасится его же кнопка. */
  pendingId: string | null;
}

/**
 * Подписанная ссылка на вложение задачи (ТЗ 0066).
 *
 * ВСЕГДА `no-cache` через `client.query`: ссылка живёт пять минут, и
 * закешированная — это и битая ссылка, и bearer-токен в дампе состояния. По той же причине поля `url` нет в самом
 * типе вложения: иначе bearer-ссылка приезжала бы в каждом ответе ленты,
 * оседала в кеше Apollo и в breadcrumbs Sentry, а её срок начинал бы тикать
 * в момент показа списка, а не в момент, когда файл понадобился.
 *
 * Тост принадлежит хуку, а не компоненту — домашний приём (ср.
 * `useHomeworkDownloadUrl`).
 */
export const useTaskAttachmentUrl = (): ITaskAttachmentUrlHook => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { addToast } = useToasts();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const resolveUrl = useCallback(
    async (attachmentId: string): Promise<string | null> => {
      setPendingId(attachmentId);
      try {
        const result = await client.query<
          TaskAttachmentLinkQuery,
          TaskAttachmentLinkQueryVariables
        >({
          query: TaskAttachmentLinkDocument,
          variables: { attachmentId },
          // 🔴 `no-cache`, а НЕ `network-only`. Оба ходят в сеть, но
          // `network-only` результат в кеш ЗАПИСЫВАЕТ: замер на стенде показал
          // подписанную ссылку в `ROOT_QUERY` после первого же клика. Это ровно
          // то, ради чего у типа вложения нет поля `url` — bearer-ссылка не
          // должна оседать в кеше и уезжать в дампы состояния.
          fetchPolicy: 'no-cache',
        });
        const url = result.data?.staffTaskAttachmentUrl?.url;
        if (url) return url;
      } catch (e) {
        // `errorPolicy` клиента — `none`: сервер отвечает ошибкой на файл,
        // до которого человеку нет доступа, и промис ОТВЕРГАЕТСЯ.
      } finally {
        setPendingId(null);
      }
      addToast({
        type: GlobalToastTypeNames.TaskQuickAction,
        data: {
          type: ToastTypes.error,
          title: t('pages.tasks.files.openFailed'),
        },
      });
      return null;
    },
    [client, addToast, t],
  );

  const openFile = useCallback(
    async (attachmentId: string) => {
      const url = await resolveUrl(attachmentId);
      if (url) window.open(url, '_blank', 'noopener');
    },
    [resolveUrl],
  );

  return { resolveUrl, openFile, pendingId };
};
